import { createElement, useCallback, type UIEvent } from 'react';

import { app, checkout, RootState, type CheckoutViews } from 'core';
import { isMobile } from 'core/use-cases/app/helpers';
import { useSelector } from 'core/use-cases/utilities';
import { useNavigate, useLocation } from 'react-router';
import {
  HeaderCheckout,
  HeaderCheckoutButton,
  HeaderCheckoutLogoButton,
  HeaderCheckoutProgressSteps,
  ResponsiveFloraTypography,
  Step,
} from 'ui/components';
import { Logo } from 'ui/containers/SectionBuilder/sections/Logo';
import { useRedirectToHome } from 'ui/hooks/useRedirectToHome';

import { checkoutStepsMapper } from './steps-mapper';
import { parseIndexViews } from './utilities';
import { useCheckout } from '../hooks';
import { Modal } from '../Modal';

import type { MapperStepEvent, Steps } from './HeaderCheckout.d';

const eventAction = 'clique:botao';
const eventCategory = 'carrinho:revisao';
const eventLabel = 'os-dados-do-cartao-serao-apagados';
const interactionModal = 'interacao:modal';

enum PATHNAME {
  CART = '/carrinho',
  CHECKOUT = '/checkout',
  DEPARTAMENTS = '/departamentos',
}

const HeaderWidget = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { contentType, setContentType, isCreditCard } = useCheckout();
  const { setStep, toggleNotification } = checkout.commands.useCheckoutCommand();
  const { trackClick } = app.commands.useAppCommand();
  const { redirectToHome } = useRedirectToHome();
  const { selectedPromotions } = useSelector(({ checkout: dataCheckout }) => dataCheckout);
  const { step: currentStep, views, paymentPlanError } = checkout.hooks.useCheckout();

  const isCurrentPathCart = pathname === PATHNAME.CART;
  const isCurrentPathCheckout = pathname === PATHNAME.CHECKOUT;

  const {
    data: { isNotificationVisible },
  } = checkout.hooks.useSummary();

  const {
    data: { minOrderValueNum, minOrderValuePendingNum },
  } = useSelector((state: RootState) => state.cart);

  const progressRemaining = minOrderValueNum - minOrderValuePendingNum;
  const isCompleted = progressRemaining >= minOrderValueNum;

  const stepKeys = Object.keys(views) as [Steps];

  const hasGiftSelected = selectedPromotions?.length > 0;
  const isPageReviewAndIsPaymentMethodCreditCard = currentStep === 'summary' && isCreditCard;

  const handleBackToCart = useCallback((event: UIEvent<HTMLButtonElement>) => {
    trackClick(event);

    if (isCurrentPathCheckout) {
      if (hasGiftSelected || isPageReviewAndIsPaymentMethodCreditCard) {
        toggleNotification();
        setContentType('redirectToCart');

        return;
      }
    }

    navigate(PATHNAME.DEPARTAMENTS);
  }, []);

  const handleOnClickToHome = useCallback((event: UIEvent<HTMLButtonElement>) => {
    trackClick(event);

    if ((hasGiftSelected || isPageReviewAndIsPaymentMethodCreditCard) && !paymentPlanError) {
      toggleNotification();
      setContentType('redirectToHome');

      return;
    }

    redirectToHome();
  }, []);

  const handleOnClose = useCallback((event: UIEvent<HTMLButtonElement>) => {
    const label = event.currentTarget.innerText || 'fechar';
    setContentType('');
    toggleNotification();

    trackClick(event, 'event', {
      eventAction,
      eventCategory: interactionModal,
      eventLabel: `${eventLabel}:${label}`,
    });

    trackClick(event, 'event', {
      eventAction: interactionModal,
      eventCategory,
      eventLabel: `fechar:${eventLabel}`,
    });
  }, []);

  const handleChangePaymentMethod = useCallback(
    (step: Steps) => {
      if (isPageReviewAndIsPaymentMethodCreditCard) {
        setContentType('default');
        toggleNotification();

        return;
      }

      if (isCompleted) {
        setStep(step);

        if (!isMobile()) {
          navigate(PATHNAME.CHECKOUT);
        }
      }
    },

    [isPageReviewAndIsPaymentMethodCreditCard, minOrderValuePendingNum],
  );

  const goToBackCart = (step: keyof CheckoutViews) => {
    navigate(PATHNAME.CART);
    setStep(step);
  };

  const mapperStepsEvents: MapperStepEvent = {
    cart: (event, step) => (isMobile() ? handleBackToCart(event) : goToBackCart(step)),
    payment: (_, step) => handleChangePaymentMethod(step),
    summary: (_, step) => setStep(step),
  };

  return (
    <>
      <HeaderCheckout>
        <HeaderCheckoutLogoButton data-checkout-logo-button onClick={handleOnClickToHome}>
          <Logo />
        </HeaderCheckoutLogoButton>
        <HeaderCheckoutButton onClick={handleBackToCart} size="small">
          <ResponsiveFloraTypography fontSize="bodySmallStandard" fontWeight="regular">
            Voltar ao inicio
          </ResponsiveFloraTypography>
        </HeaderCheckoutButton>
        <HeaderCheckoutProgressSteps
          current={parseIndexViews(views)[isCurrentPathCart ? 'cart' : currentStep]}
        >
          {stepKeys.map((step) => {
            const { icon, name } = checkoutStepsMapper[step];

            return (
              <Step
                key={step}
                onClick={(event) => mapperStepsEvents[step](event, step)}
                title={name}
              >
                {createElement(icon)}
              </Step>
            );
          })}
        </HeaderCheckoutProgressSteps>
      </HeaderCheckout>
      {isNotificationVisible && !paymentPlanError && (
        <Modal isOpen={isNotificationVisible} onClose={handleOnClose} contentType={contentType} />
      )}
    </>
  );
};

export default HeaderWidget;
